import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, DatePicker, Empty, Modal, Select, Spin, Table, Tag, message } from 'antd';
import { format } from 'date-fns';
import moment from 'moment';
import get from 'lodash.get';
import { useDispatch, useSelector } from 'react-redux';
import { GET } from '../../../service/base-api-service';
import { getUser } from '../../../features/auth.store';
import { ROLE } from '../../../utils/features';
import { MINE_ARC_SERVICE_TECHNICIAN_ROLE_ID } from '../../../utils/constant';
import { getIssueLists } from '../../../utils/utils';
import SubPageLayout from '../helpers/SubPageLayout';
import AsrSessionDetail from './asr/AsrSessionDetail';
import { downloadAsrPdf } from './asr/asrPdfGenerator';
import { applySiteTechnicians } from './asr/siteTechnicians';
import {
  createAsrSession,
  deleteAsrSession,
  fetchAsrSessions,
  updateAsrSession,
  updateAsrSessionChamber,
} from '../../../features/asr.store';
import './styles/reports.scss';

const normalizeRoles = (user) => {
  const scope = get(user, 'scope');
  const role = get(user, 'role');
  const roles = get(user, 'roles', []);
  const authorities = get(user, 'authorities', []);
  const authorityNames = Array.isArray(authorities)
    ? authorities.map((item) => (typeof item === 'string' ? item : item?.authority)).filter(Boolean)
    : [];

  const splitScope = typeof scope === 'string' ? scope.split(',').map((s) => s.trim()).filter(Boolean) : [];

  return Array.from(new Set([...splitScope, ...(role ? [role] : []), ...(Array.isArray(roles) ? roles : []), ...authorityNames]));
};

const hasRole = (user, roleConst) => normalizeRoles(user).includes(roleConst);

const getTenantIdentifierFromUser = (user) =>
  get(user, 'tenant.identifier') ||
  get(user, 'tenant.id') ||
  get(user, 'tenantIdentifier') ||
  get(user, 'tenant_identifier') ||
  get(user, 'tenantId') ||
  '';

const getTenantNameFromUser = (user) =>
  get(user, 'tenant.name') ||
  get(user, 'tenantName') ||
  get(user, 'tenant_name') ||
  '';

const normalizeList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.list)) return payload.list;
  return [];
};

const getSiteIdFromRecord = (site) =>
  get(site, 'id') ||
  get(site, 'siteId') ||
  get(site, 'site_id') ||
  get(site, 'identifier') ||
  null;

const getSiteNameFromRecord = (site) =>
  get(site, 'name') ||
  get(site, 'siteName') ||
  get(site, 'site_name') ||
  get(site, 'displayName') ||
  '';

const getSiteTenantFromRecord = (site) =>
  get(site, 'tenant.identifier') ||
  get(site, 'tenantIdentifier') ||
  get(site, 'tenantId') ||
  get(site, 'tenant_id') ||
  '';

const toTenantOptions = (payload) =>
  (Array.isArray(payload) ? payload : [])
    .map((item) => ({
      value: get(item, 'identifier') || get(item, 'id') || '',
      label: get(item, 'name') || get(item, 'identifier') || get(item, 'id') || '',
    }))
    .filter((item) => item.value)
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));

const dedupeOptions = (options) => Array.from(new Map(options.map((item) => [String(item.value), item])).values());

const toDate = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  const candidate = Number.isNaN(numeric) ? new Date(value) : new Date(numeric);
  return Number.isNaN(candidate.getTime()) ? null : candidate;
};

const formatLastServiced = (value) => {
  const parsed = toDate(value);
  if (!parsed) return 'Not recorded';
  return format(parsed, 'dd/MM/yyyy');
};

const loadLatestServiceForm = async (chamberName) => {
  if (!chamberName) return null;

  try {
    return await GET('v1/chamber/serviceForm/latest', {
      params: { chamberName },
    });
  } catch (err) {
    if (err?.status !== 404) {
      console.error(`Failed to load latest service form for ${chamberName}`, err);
    }
    return null;
  }
};

const loadSiteDetails = async (siteId) => {
  if (!siteId) return {};
  for (const url of [`v1/admin/site/${siteId}`, `v1/site/${siteId}`]) {
    try {
      const data = await GET(url);
      if (data) return data;
    } catch (err) {
      // Try the next endpoint.
    }
  }
  return {};
};

const getCompletingUser = (user) => ({
  preparedBy:
    get(user, 'user_full_name') || get(user, 'fullName') || get(user, 'name') || '',
  preparedByEmail:
    get(user, 'email') || get(user, 'user_email') || get(user, 'preferred_username') || get(user, 'sub') || '',
  preparedByTelephone:
    get(user, 'user_telephone_number') || get(user, 'telephoneNumber') || get(user, 'phone') || '',
});

const normalizeUsersList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.list)) return payload.list;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.users)) return payload.users;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

const mapTechnicianOptions = (users = []) =>
  dedupeOptions(
    users
      .map((item) => {
        const value = String(
          get(item, 'id') ||
            get(item, 'userId') ||
            get(item, 'identifier') ||
            get(item, 'userID') ||
            '',
        );

        if (!value) return null;

        const label =
          get(item, 'fullName') ||
          get(item, 'name') ||
          get(item, 'email') ||
          get(item, 'username') ||
          `User ${value}`;

        return { value, label };
      })
      .filter(Boolean),
  ).sort((a, b) => String(a.label).localeCompare(String(b.label)));

const isMineArcServiceTechnician = (userRecord = {}) => {
  const roleId = String(
    get(userRecord, 'role.id') ||
      get(userRecord, 'roleId') ||
      get(userRecord, 'role_id') ||
      get(userRecord, 'roleID') ||
      get(userRecord, 'roleName') ||
      get(userRecord, 'role_name') ||
      get(userRecord, 'role.displayName') ||
      get(userRecord, 'role.name') ||
      '',
  );

  if (
    String(roleId) === String(MINE_ARC_SERVICE_TECHNICIAN_ROLE_ID) ||
    String(roleId).includes(String(MINE_ARC_SERVICE_TECHNICIAN_ROLE_ID))
  ) {
    return true;
  }

  const nestedValues = [];
  const walk = (value) => {
    if (value === null || value === undefined) return;

    if (typeof value === 'string') {
      nestedValues.push(value);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => walk(item));
      return;
    }

    if (typeof value === 'object') {
      Object.values(value).forEach((item) => walk(item));
    }
  };

  walk(userRecord);

  return nestedValues
    .map((value) => String(value).toUpperCase())
    .some((value) => {
      const hasMineArc = value.includes('MINEARC') || value.includes('MINE ARC') || value.includes('MINE_ARC');
      const hasTechnician = value.includes('TECHNICIAN');
      const hasRoleMarker =
        value.includes('ROLE_MINE_ARC_SERVICE_TECHNICIAN') ||
        value.includes('ROLE_MINEARC_SERVICE_TECHNICIAN') ||
        value.includes('MINEARC_SERVICE_TECHNICIAN');

      return (hasMineArc && hasTechnician) || hasRoleMarker;
    });
};

const SiteAsr = () => {
  const dispatch = useDispatch();
  const user = useSelector(getUser);

  const isSuperAdminRole = useMemo(() => hasRole(user, ROLE.SA), [user]);
  const isMinearcRole = useMemo(() => hasRole(user, ROLE.MINEARC), [user]);
  const isTenantRole = useMemo(() => hasRole(user, ROLE.TA), [user]);
  const isReadOnlyAsrRole = useMemo(() => isTenantRole || hasRole(user, ROLE.SU), [isTenantRole, user]);

  const canSwitchTenant = isSuperAdminRole || isMinearcRole;

  const userTenantId = useMemo(() => getTenantIdentifierFromUser(user), [user]);
  const userTenantName = useMemo(() => getTenantNameFromUser(user), [user]);

  const [tenantOptions, setTenantOptions] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);

  const [selectedTenant, setSelectedTenant] = useState('');
  const [selectedSite, setSelectedSite] = useState('');

  const [loadingTenants, setLoadingTenants] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);
  const [loadingAsrData, setLoadingAsrData] = useState(false);
  const [error, setError] = useState('');

  const [asrModalVisible, setAsrModalVisible] = useState(false);
  const [asrChamberRows, setAsrChamberRows] = useState([]);
  const [totalSiteChambers, setTotalSiteChambers] = useState(0);
  const [technicianOptions, setTechnicianOptions] = useState([]);
  const [siteTechnicianIds, setSiteTechnicianIds] = useState([]);
  const [visitStartDateTime, setVisitStartDateTime] = useState(null);
  const [visitEndDateTime, setVisitEndDateTime] = useState(null);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [reportDetails, setReportDetails] = useState({});

  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [sessionMonth, setSessionMonth] = useState(null);

  const visibleSessions = useMemo(() => {
    const completedSessions = isReadOnlyAsrRole
      ? sessions.filter((session) => String(session.status || '').toLowerCase() === 'completed')
      : sessions;

    if (!sessionMonth) return completedSessions;

    return completedSessions.filter((session) => {
      const visitDate = toDate(session.visitStart || session.completedAt || session.createdAt);
      return visitDate && visitDate.getFullYear() === sessionMonth.year() && visitDate.getMonth() === sessionMonth.month();
    });
  }, [isReadOnlyAsrRole, sessionMonth, sessions]);

  const activeSession = useMemo(
    () => sessions.find((s) => String(s.id) === String(activeSessionId)) || null,
    [sessions, activeSessionId],
  );

  const refreshSessions = useCallback(async () => {
    if (!selectedTenant || !selectedSite) {
      setSessions([]);
      return;
    }
    try {
      const result = await dispatch(fetchAsrSessions({ tenantId: selectedTenant, siteId: selectedSite })).unwrap();
      setSessions(result || []);
    } catch (err) {
      setSessions([]);
      setError(err?.message || 'Failed to load ASR sessions.');
    }
  }, [dispatch, selectedTenant, selectedSite]);

  useEffect(() => {
    refreshSessions();
  }, [refreshSessions]);

  const selectedSiteOption = useMemo(
    () => siteOptions.find((item) => String(item.value) === String(selectedSite || '')) || null,
    [siteOptions, selectedSite],
  );

  const loadTenants = useCallback(async () => {
    setError('');

    if (!canSwitchTenant) {
      const option = userTenantId
        ? [{ value: userTenantId, label: userTenantName || userTenantId }]
        : [];
      setTenantOptions(option);
      setSelectedTenant(option[0]?.value || '');
      return;
    }

    setLoadingTenants(true);
    try {
      const payload = await GET('v1/admin/tenant/minimal');
      const options = toTenantOptions(payload);
      setTenantOptions(options);
      setSelectedTenant((current) => current || options[0]?.value || '');
    } catch (err) {
      setTenantOptions([]);
      setSelectedTenant('');
      setError('Failed to load tenants.');
    } finally {
      setLoadingTenants(false);
    }
  }, [canSwitchTenant, userTenantId, userTenantName]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  useEffect(() => {
    let cancelled = false;

    const loadSites = async () => {
      setSelectedSite('');
      setSiteOptions([]);

      if (!selectedTenant) return;

      setLoadingSites(true);
      setError('');

      const attempts = [
        {
          path: 'v1/admin/site/minimal',
          params: { tenantId: selectedTenant },
        },
        {
          path: 'v1/admin/site',
          params: { page: 0, size: 10000, sortColumn: 'name', isAscending: true, tenantId: selectedTenant, searchKey: '' },
        },
        {
          path: 'v1/site/all',
          params: { page: 0, size: 10000, sortColumn: 'name', isAscending: true, tenantId: selectedTenant, searchKey: '' },
        },
        {
          path: 'v1/site',
          params: { tenantId: selectedTenant },
        },
      ];

      for (const attempt of attempts) {
        try {
          const payload = await GET(attempt.path, { params: attempt.params });
          const rows = normalizeList(payload);
          if (!rows.length) continue;

          const mapped = rows
            .map((site) => {
              const id = getSiteIdFromRecord(site);
              if (id === null || id === undefined || id === '') return null;
              const name = getSiteNameFromRecord(site);
              const tenantId = getSiteTenantFromRecord(site);

              return {
                value: String(id),
                label: name || String(id),
                tenantId: tenantId ? String(tenantId) : '',
              };
            })
            .filter(Boolean)
            .filter((site) => !site.tenantId || site.tenantId === String(selectedTenant));

          if (mapped.length > 0) {
            if (!cancelled) {
              setSiteOptions(dedupeOptions(mapped));
            }
            return;
          }
        } catch (err) {
          // Try the next endpoint.
        }
      }

      if (!cancelled) {
        setSiteOptions([]);
      }
    };

    loadSites()
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load sites for the selected tenant.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingSites(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedTenant]);

  const loadChambersForSite = useCallback(async (siteId) => {
    const attempts = [
      {
        path: `v1/admin/site/chambers/${siteId}`,
        params: { page: 0, size: 10000, sortColumn: 'name', isAscending: true },
      },
      {
        path: `v1/site/chambers/${siteId}`,
        params: { page: 0, size: 10000, sortColumn: 'name', isAscending: true },
      },
    ];

    for (const attempt of attempts) {
      try {
        const payload = await GET(attempt.path, { params: attempt.params });
        const rows = normalizeList(payload);
        if (!rows.length) continue;

        return rows
          .map((item, idx) => {
            const chamberId =
              get(item, 'id') ||
              get(item, 'chamberId') ||
              get(item, 'identifier') ||
              null;
            const chamberName =
              get(item, 'name') ||
              get(item, 'chamberName') ||
              get(item, 'chamberDisplayId') ||
              `Chamber ${idx + 1}`;
            const lastServicedDate =
              get(item, 'lastServicedDate') ||
              get(item, 'lastServiceDate') ||
              get(item, 'servicedDate') ||
              get(item, 'serviceDate') ||
              null;

            return {
              chamberId: String(chamberId || `${chamberName}-${idx}`),
              chamberName,
              lastServicedDate,
              chamberType: get(item, 'chamberTypeEnum.displayName') || get(item, 'chamberType') || '',
              capacity: get(item, 'capacity') || get(item, 'size') || '',
              scrubberType:
                get(item, 'scrubberTypeEnum.displayName') ||
                get(item, 'scrubberTypeEnum.name') ||
                get(item, 'scrubberType') ||
                '',
              nextCalibrationDate:
                get(item, 'nextCalibrationDate') ||
                get(item, 'nextCalibrationDueDate') ||
                get(item, 'calibrationDueDate') ||
                null,
            };
          })
          .sort((a, b) => String(a.chamberName).localeCompare(String(b.chamberName), undefined, { numeric: true, sensitivity: 'base' }));
      } catch (err) {
        // Try next endpoint.
      }
    }

    return [];
  }, []);

  const loadTechnicians = useCallback(async () => {
    const defaultParams = {
      page: 0,
      size: 10000,
      sortColumn: 'fullName',
      isAscending: true,
      searchKey: '',
    };

    const attempts = [
      {
        path: 'v1/admin/users',
        params: defaultParams,
      },
      {
        path: 'v1/admin/users/technicians',
        params: defaultParams,
      },
      {
        path: 'v1/admin/users',
        params: { ...defaultParams, tenantId: selectedTenant },
      },
      {
        path: 'v1/admin/users/technicians',
        params: { ...defaultParams, tenantId: selectedTenant },
      },
    ].filter((attempt) => attempt.path);

    for (const attempt of attempts) {
      try {
        console.log('[SiteAsr] fetching technicians from:', attempt.path, attempt.params || {});
        const payload = await GET(attempt.path, { params: attempt.params });
        console.log('[SiteAsr] technicians payload for', attempt.path, payload);
        const users = normalizeUsersList(payload);
        if (!users.length) continue;

        const sourceUsers = users.filter((item) => isMineArcServiceTechnician(item));
        console.log('[SiteAsr] technicians filtered count for', attempt.path, sourceUsers.length);
        const mapped = mapTechnicianOptions(sourceUsers);
        if (mapped.length > 0) {
          console.log('[SiteAsr] technicians selected for dropdown:', mapped);
          return mapped;
        }
      } catch (err) {
        console.error('[SiteAsr] failed fetching technicians from', attempt.path, err);
      }
    }

    return [];
  }, [selectedTenant]);

  const openAsrModal = useCallback(
    async (sessionToEdit = null) => {
      if (!selectedSite) return;

      setAsrModalVisible(true);
      setLoadingAsrData(true);
      setError('');
      setEditingSessionId(sessionToEdit?.id || null);
      setSiteTechnicianIds([]);

      try {
        const [chambers, technicians, siteDetails] = await Promise.all([
          loadChambersForSite(selectedSite),
          loadTechnicians(),
          loadSiteDetails(selectedSite),
        ]);

        const existingChamberById = new Map(
          (sessionToEdit?.chambers || []).map((chamber) => [String(chamber.chamberId), chamber]),
        );

        const baseRows = sessionToEdit
          ? sessionToEdit.chambers.map((chamber) => ({
              ...chamber,
              technicianIds: chamber.technicianIds || [],
            }))
          : chambers.map((chamber) => ({
              ...chamber,
              technicianIds: existingChamberById.get(String(chamber.chamberId))?.technicianIds || [],
            }));

        const rows = await Promise.all(
          baseRows.map(async (chamber) => {
            const latestServiceForm = await loadLatestServiceForm(chamber.chamberName);
            if (!latestServiceForm) return chamber;

            const { issuesList = [] } = getIssueLists(latestServiceForm);
            const existingIssues = existingChamberById.get(String(chamber.chamberId))?.issues || [];
            const existingByQuestion = new Map(existingIssues.map((issue) => [issue.question, issue]));

            return {
              ...chamber,
              latestServiceForm,
              serviceFormId: latestServiceForm.serviceFormId || null,
              sourceServicedDate: latestServiceForm.servicedDate || null,
              chamberLocation: latestServiceForm.chamberLocation || 'Not applicable',
              batteryVoltages:
                get(latestServiceForm, 'consumables.batteryVoltages.recordBatteryVoltages', []) || [],
              issues: issuesList.map((issue) => ({
                ...issue,
                solution: existingByQuestion.get(issue.question)?.solution || '',
              })),
            };
          }),
        );

        setAsrChamberRows(rows);
        setTotalSiteChambers(chambers.length);
        setTechnicianOptions(technicians);
        setVisitStartDateTime(sessionToEdit?.visitStart ? moment(sessionToEdit.visitStart) : null);
        setVisitEndDateTime(sessionToEdit?.visitEnd ? moment(sessionToEdit.visitEnd) : null);
        setReportDetails({
          siteName: selectedSiteOption?.label || siteDetails?.name || '',
          attendeeName: get(siteDetails, 'mainContact.fullName') || get(siteDetails, 'mainContact.name') || '',
          attendeeEmail: get(siteDetails, 'mainContact.email') || '',
          ccEmail: get(siteDetails, 'secondContact.email') || '',
          preparedBy: '',
          preparedByEmail: '',
          preparedByTelephone: '',
          quoteNumber: '',
          ...(sessionToEdit?.reportDetails || {}),
        });
      } catch (err) {
        setError('Unable to load ASR form data. Please try again.');
      } finally {
        setLoadingAsrData(false);
      }
    },
    [loadChambersForSite, loadTechnicians, selectedSite, selectedSiteOption],
  );

  const removeAsrChamber = useCallback((chamberId) => {
    setAsrChamberRows((prev) => prev.filter((row) => String(row.chamberId) !== String(chamberId)));
  }, []);

  const updateSiteTechnicians = (technicianIds) => {
    setAsrChamberRows((prev) => applySiteTechnicians(prev, siteTechnicianIds, technicianIds));
    setSiteTechnicianIds(technicianIds);
  };

  const updateChamberTechnicians = useCallback((chamberId, technicianIds) => {
    setAsrChamberRows((prev) =>
      prev.map((row) =>
        String(row.chamberId) === String(chamberId)
          ? { ...row, technicianIds: Array.isArray(technicianIds) ? technicianIds : [] }
          : row,
      ),
    );
  }, []);

  const submitAsrDraft = useCallback(async () => {
    if (!visitStartDateTime || !visitEndDateTime) {
      message.warning('Please select Service Visit start and end dates.');
      return;
    }

    if (visitEndDateTime.isBefore(visitStartDateTime)) {
      message.warning('End date/time must be after start date/time.');
      return;
    }

    const technicianNameById = new Map(technicianOptions.map((option) => [String(option.value), option.label]));
    const existingSession = editingSessionId ? sessions.find((s) => String(s.id) === String(editingSessionId)) : null;
    const existingChamberById = new Map((existingSession?.chambers || []).map((c) => [String(c.chamberId), c]));

    const chambers = asrChamberRows.map((row) => {
      const previous = existingChamberById.get(String(row.chamberId));
      return {
        chamberId: row.chamberId,
        chamberName: row.chamberName,
        lastServicedDate: row.lastServicedDate,
        technicianIds: row.technicianIds || [],
        technicianNames: (row.technicianIds || []).map((id) => technicianNameById.get(String(id))).filter(Boolean),
        sourceServicedDate: row.sourceServicedDate || previous?.sourceServicedDate || null,
        latestServiceForm: row.latestServiceForm || previous?.latestServiceForm || null,
        serviceFormId: row.serviceFormId || previous?.serviceFormId || null,
        chamberLocation: row.chamberLocation || previous?.chamberLocation || 'Not applicable',
        batteryVoltages: row.batteryVoltages || previous?.batteryVoltages || [],
        chamberType: row.chamberType || previous?.chamberType || '',
        capacity: row.capacity || previous?.capacity || '',
        scrubberType: row.scrubberType || previous?.scrubberType || '',
        nextCalibrationDate: row.nextCalibrationDate || previous?.nextCalibrationDate || null,
        issues: row.issues || previous?.issues || [],
      };
    });

    const sessionPayload = {
      tenantId: selectedTenant,
      tenantName: tenantOptions.find((item) => String(item.value) === String(selectedTenant))?.label || selectedTenant,
      siteId: selectedSite,
      siteName: selectedSiteOption?.label || selectedSite,
      visitStart: visitStartDateTime.clone().startOf('day').toISOString(),
      visitEnd: visitEndDateTime.clone().endOf('day').toISOString(),
      status: existingSession?.status || 'draft',
      chambers,
      reportDetails,
    };

    try {
      if (editingSessionId) {
        await dispatch(updateAsrSession({ id: editingSessionId, patch: sessionPayload })).unwrap();
        message.success('ASR session updated.');
      } else {
        await dispatch(createAsrSession(sessionPayload)).unwrap();
        message.success('ASR session created. Open it to fill in chamber issues.');
      }
    } catch (err) {
      message.error(err?.message || 'Unable to save ASR session.');
      return;
    }

    refreshSessions();
    setAsrModalVisible(false);
  }, [
    asrChamberRows,
    dispatch,
    editingSessionId,
    refreshSessions,
    selectedSite,
    selectedSiteOption,
    selectedTenant,
    sessions,
    technicianOptions,
    tenantOptions,
    reportDetails,
    visitEndDateTime,
    visitStartDateTime,
  ]);

  const handleOpenSession = useCallback((session) => {
    if (isReadOnlyAsrRole) return;
    setActiveSessionId(session.id);
  }, [isReadOnlyAsrRole]);

  const handleEditSession = useCallback(
    (session) => {
      if (isReadOnlyAsrRole) return;
      openAsrModal(session);
    },
    [isReadOnlyAsrRole, openAsrModal],
  );

  const handleToggleComplete = useCallback(
    async (session, completed) => {
      if (isReadOnlyAsrRole) return;
      try {
        await dispatch(updateAsrSession({ id: session.id, patch: {
        status: completed ? 'completed' : 'draft',
        ...(completed
          ? {
              completedAt: new Date().toISOString(),
              reportDetails: {
                ...(session.reportDetails || {}),
                ...getCompletingUser(user),
              },
            }
          : {}),
        } })).unwrap();
        await refreshSessions();
        message.success(completed ? 'ASR session marked as completed.' : 'ASR session reopened.');
      } catch (err) {
        message.error(err?.message || 'Unable to update ASR session.');
      }
    },
    [dispatch, isReadOnlyAsrRole, refreshSessions, user],
  );

  const handleDeleteSession = useCallback(
    async (session) => {
      if (isReadOnlyAsrRole) return;
      try {
        await dispatch(deleteAsrSession(session.id)).unwrap();
        if (String(activeSessionId) === String(session.id)) setActiveSessionId(null);
        await refreshSessions();
        message.success('ASR session deleted.');
      } catch (err) {
        message.error(err?.message || 'Unable to delete ASR session.');
      }
    },
    [activeSessionId, dispatch, isReadOnlyAsrRole, refreshSessions],
  );

  const handleSaveChamberIssues = useCallback(
    async (sessionId, chamberId, patch) => {
      const { reportDetails: updatedReportDetails, ...chamberPatch } = patch;
      try {
        await dispatch(updateAsrSessionChamber({ sessionId, chamberId, patch: chamberPatch })).unwrap();
        if (updatedReportDetails) {
          await dispatch(updateAsrSession({ id: sessionId, patch: { reportDetails: updatedReportDetails } })).unwrap();
        }
        await refreshSessions();
      } catch (err) {
        message.error(err?.message || 'Unable to save chamber issues.');
      }
    },
    [dispatch, refreshSessions],
  );

  const handleDownloadSession = useCallback(async (session) => {
    try {
      await downloadAsrPdf(session);
    } catch (err) {
      console.error('Unable to generate Site ASR PDF', err);
      message.error('Unable to download the Site ASR report. Please try again.');
    }
  }, []);

  const asrColumns = useMemo(
    () => [
      {
        title: 'Chamber',
        dataIndex: 'chamberName',
        key: 'chamberName',
        width: '24%',
      },
      {
        title: 'Last Service Date',
        dataIndex: 'lastServicedDate',
        key: 'lastServicedDate',
        width: '17%',
        render: (value) => <Tag className="site-asr-date-tag">{formatLastServiced(value)}</Tag>,
      },
      {
        title: 'MineARC Service Technician(s)',
        key: 'technicians',
        render: (_, row) => (
          <Select
            mode="multiple"
            className="site-asr-tech-select"
            placeholder="Select technician(s)"
            value={row.technicianIds || []}
            onChange={(value) => updateChamberTechnicians(row.chamberId, value)}
            options={technicianOptions}
            showSearch
            optionFilterProp="label"
            filterOption={(input, option) =>
              String(option?.label || '')
                .toLowerCase()
                .includes(String(input || '').toLowerCase())
            }
          />
        ),
      },
      {
        title: '',
        key: 'remove',
        width: '13%',
        render: (_, row) => (
          <Button
            danger
            type="default"
            size="small"
            className="site-asr-remove-btn"
            onClick={() => removeAsrChamber(row.chamberId)}
          >
            Remove
          </Button>
        ),
      },
    ],
    [removeAsrChamber, technicianOptions, updateChamberTechnicians],
  );

  return (
    <SubPageLayout title="Reports" subtitle="Site ASR">
      <div className="reports poppins">
        <div className="reports-filter-bar site-asr-filter-bar d-flex ai-center">
          <div className="reports-filter-field site-asr-field">
            <Select
              className="w-100"
              placeholder="Select Tenant"
              value={selectedTenant || undefined}
              options={tenantOptions}
              onChange={(value) => setSelectedTenant(value || '')}
              loading={loadingTenants}
              disabled={loadingTenants || (!canSwitchTenant && isTenantRole && Boolean(userTenantId))}
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                String(option?.label || '')
                  .toLowerCase()
                  .includes(String(input || '').toLowerCase())
              }
            />
          </div>

          <div className="reports-filter-field site-asr-field">
            <Select
              className="w-100"
              placeholder={selectedTenant ? 'Select Site' : 'Select Tenant first'}
              value={selectedSite || undefined}
              options={siteOptions}
              onChange={(value) => setSelectedSite(value || '')}
              loading={loadingSites}
              disabled={!selectedTenant || loadingSites}
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                String(option?.label || '')
                  .toLowerCase()
                  .includes(String(input || '').toLowerCase())
              }
            />
          </div>

          {!isReadOnlyAsrRole && (
            <div className="reports-filter-field site-asr-generate-wrap">
              <Button
                type="primary"
                className="site-asr-generate-btn"
                disabled={!selectedSiteOption}
                onClick={() => openAsrModal()}
              >
                Generate ASR
              </Button>
            </div>
          )}
        </div>

        {error && (
          <Alert
            type="warning"
            showIcon
            message={error}
            style={{ marginTop: 12 }}
          />
        )}

        {/* {selectedSiteOption && (
          <div className="site-asr-selected-note">
            Ready for ASR generation: {selectedSiteOption.label}
          </div>
        )} */}

        {selectedSiteOption && (
          <div className="asr-sessions-section">
            <div className="asr-sessions-heading-row">
              <div>
                {/* <div className="asr-sessions-heading">ASR Sessions</div> */}
                <div className="asr-sessions-caption">{selectedSiteOption.label}</div>
              </div>
              <DatePicker
                picker="month"
                allowClear
                format="MMMM YYYY"
                placeholder="Filter by month"
                value={sessionMonth}
                onChange={setSessionMonth}
                className="asr-session-month-filter"
              />
            </div>

            {visibleSessions.length === 0 ? (
              <Empty description={isReadOnlyAsrRole ? 'No completed ASR sessions found for this period.' : 'No ASR sessions found for this period.'} />
            ) : (
              <div className="asr-sessions-grid">
                {visibleSessions.map((session) => {
                  const nestedChamberCount = (session.chambers || []).length;
                  const nestedTechnicianCount = new Set(
                    (session.chambers || []).flatMap((c) => c.technicianIds || []),
                  ).size;
                  const chamberCount = Number(
                    session.chamberCount ?? session.totalChambers ?? session.numberOfChambers ?? nestedChamberCount,
                  );
                  const techCount = Number(
                    session.technicianCount ??
                      session.totalTechnicians ??
                      session.numberOfTechnicians ??
                      nestedTechnicianCount,
                  );

                  return (
                    <div
                      key={session.id}
                      className={`asr-session-card ${isReadOnlyAsrRole ? 'read-only' : ''}`}
                      onClick={() => handleOpenSession(session)}
                      role={isReadOnlyAsrRole ? undefined : 'button'}
                      tabIndex={isReadOnlyAsrRole ? undefined : 0}
                    >
                      <div className="asr-session-card-header">
                        <Tag color={session.status === 'completed' ? 'green' : 'gold'}>
                          {session.status === 'completed' ? 'Completed' : 'Draft'}
                        </Tag>
                        <span className="asr-session-dates">
                          {formatLastServiced(session.visitStart)} → {formatLastServiced(session.visitEnd)}
                        </span>
                      </div>
                      <div className="asr-session-card-stats">
                        <div>
                          <strong>{chamberCount}</strong> chamber{chamberCount === 1 ? '' : 's'}
                        </div>
                        <div>
                          <strong>{techCount}</strong> technician{techCount === 1 ? '' : 's'}
                        </div>
                      </div>
                      <div className="asr-session-card-actions" onClick={(e) => e.stopPropagation()}>
                        {!isReadOnlyAsrRole && (
                          <>
                            <Button type="primary" onClick={() => handleEditSession(session)}>
                              Edit
                            </Button>
                            <Button type="primary" onClick={() => handleToggleComplete(session, session.status !== 'completed')}>
                              {session.status === 'completed' ? 'Reopen' : 'Complete'}
                            </Button>
                          </>
                        )}
                        {session.status === 'completed' && (
                          <Button type="primary" onClick={() => handleDownloadSession(session)}>
                            Download
                          </Button>
                        )}
                        {!isReadOnlyAsrRole && session.status !== 'completed' && (
                          <Button
                            type="primary"
                            danger
                            className="asr-delete-btn"
                            onClick={() => handleDeleteSession(session)}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {(loadingTenants || loadingSites) && (
          <div className="site-asr-loading-wrap">
            <Spin size="small" />
          </div>
        )}

        <Modal
          title="Generate Site ASR"
          visible={asrModalVisible}
          width={1120}
          centered
          onCancel={() => setAsrModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setAsrModalVisible(false)}>
              Cancel
            </Button>,
            <Button
              key="generate"
              type="primary"
              className="site-asr-generate-btn"
              disabled={asrChamberRows.length === 0 || loadingAsrData}
              onClick={submitAsrDraft}
            >
              Generate ASR
            </Button>,
          ]}
          wrapClassName="site-asr-modal-wrap"
          destroyOnClose
        >
          {loadingAsrData ? (
            <div className="site-asr-modal-loading">
              <Spin />
            </div>
          ) : (
            <div className="site-asr-modal-content">
              <div className="site-asr-summary-row">
                <div className="site-asr-summary-card">
                  <div className="label">Selected Tenant</div>
                  <div className="value">
                    {tenantOptions.find((item) => String(item.value) === String(selectedTenant || ''))?.label || '-'}
                  </div>
                </div>
                <div className="site-asr-summary-card">
                  <div className="label">Selected Site</div>
                  <div className="value">{selectedSiteOption?.label || '-'}</div>
                </div>
                <div className="site-asr-summary-card emphasis">
                  <div className="label">Total Chambers In Site</div>
                  <div className="value">{totalSiteChambers}</div>
                </div>
                <div className="site-asr-summary-card">
                  <div className="label">Chambers Included In This ASR</div>
                  <div className="value">{asrChamberRows.length}</div>
                </div>
              </div>

              <div className="site-asr-visit-grid">
                <div className="site-asr-visit-field">
                  <div className="site-asr-field-label">Service Visit Start</div>
                  <DatePicker
                    className="w-100"
                    format="DD/MM/YYYY"
                    value={visitStartDateTime}
                    onChange={(value) => setVisitStartDateTime(value)}
                  />
                </div>
                <div className="site-asr-visit-field">
                  <div className="site-asr-field-label">Service Visit End</div>
                  <DatePicker
                    className="w-100"
                    format="DD/MM/YYYY"
                    value={visitEndDateTime}
                    onChange={(value) => setVisitEndDateTime(value)}
                  />
                </div>
                <div className="site-asr-visit-field site-asr-site-technicians">
                  <label className="site-asr-field-label" htmlFor="site-asr-site-technicians">
                    MineARC Service Technician(s) - Whole Site
                  </label>
                  <Select
                    id="site-asr-site-technicians"
                    mode="multiple"
                    className="w-100"
                    placeholder="Select technician(s)"
                    value={siteTechnicianIds}
                    onChange={updateSiteTechnicians}
                    options={technicianOptions}
                    showSearch
                    allowClear
                    optionFilterProp="label"
                  />
                </div>
              </div>

              <div className="site-asr-table-wrap">
                <Table
                  rowKey={(row) => row.chamberId}
                  columns={asrColumns}
                  dataSource={asrChamberRows}
                  pagination={false}
                  tableLayout="fixed"
                  locale={{ emptyText: 'No chambers available for this site.' }}
                />
              </div>
            </div>
          )}
        </Modal>

        <AsrSessionDetail
          visible={Boolean(activeSession)}
          session={activeSession}
          readOnly={isReadOnlyAsrRole}
          tenantId={selectedTenant}
          onClose={() => setActiveSessionId(null)}
          onSaveChamberIssues={handleSaveChamberIssues}
          onComplete={handleToggleComplete}
          onDownload={handleDownloadSession}
        />
      </div>
    </SubPageLayout>
  );
};

export default SiteAsr;
